import {
  Close,
  DeleteOutline,
  EditOutlined,
  EmailOutlined,
  CampaignOutlined,
  OpenInFull,
  VisibilityOutlined,
} from "@mui/icons-material";
import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Drawer,
  FormControlLabel,
  IconButton,
  MenuItem,
  Switch,
  Tab,
  Tabs,
  TextField,
  Tooltip,
  Typography,
} from "@mui/material";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import InputField from "components/Input/InputField";
import UploadFile from "components/Input/UploadFile";
import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "react-toastify";
import {
  createProgramFaq,
  createProgramOffer,
  createProgramTerms,
  deleteProgramFaq,
  deleteProgramOffer,
  deleteProgramTerms,
  fetchProgramFaqs,
  fetchProgramOffers,
  fetchProgramTerms,
  updateProgramFaq,
  updateProgramOffer,
  updateProgramTerms,
} from "./referralProgramContentApi";
import ProgramOfferPreviewDialog from "./ProgramOfferPreviewDialog";
import { businessToday, minDateFor, pastDateProblem } from "./programDates";

const TABS = [
  { id: "offers", label: "Offers" },
  { id: "terms", label: "Terms & conditions" },
  { id: "faq", label: "FAQ" },
];

const OFFER_INITIAL = {
  title: "",
  description: "",
  targetCount: "3",
  targetLabel: "admissions",
  deadline: "",
  originalDeadline: "",
  rewardAmount: "",
  rewardCurrency: "AUD",
  status: "ACTIVE",
  ctaLabel: "",
  imageUrls: [],
  showOnLoginPopup: true,
  sendViaEmail: false,
};

const termsInitial = () => ({
  version: "",
  title: "Referral partner terms",
  content: "",
  effectiveFrom: businessToday(),
  originalEffectiveFrom: "",
  isCurrent: true,
  locked: false,
  acceptedBy: 0,
});

const FAQ_INITIAL = {
  question: "",
  answer: "",
  sortOrder: "0",
  published: true,
};

const errMsg = (err, fallback) =>
  err?.response?.data?.errorMessage || err?.response?.data?.message || fallback;

const SectionHeader = ({ title, description }) => (
  <div className="mb-4">
    <Typography variant="subtitle1" className="font-semibold text-app-text">
      {title}
    </Typography>
    {description ? (
      <Typography variant="body2" color="text.secondary" className="mt-0.5">
        {description}
      </Typography>
    ) : null}
  </div>
);

const EmptyHint = ({ children }) => (
  <p className="text-sm text-app-text-muted m-0 py-6 text-center border border-dashed border-app-border rounded-xl">
    {children}
  </p>
);

const ListSkeleton = () => (
  <div className="flex justify-center py-10">
    <CircularProgress size={28} />
  </div>
);

/**
 * Admin UI to create/manage partner program content: bonus offers, T&Cs versions, and FAQs.
 * `variant="drawer"` — side panel; `variant="page"` — full admin page with two-column layout.
 */
const ReferralProgramContentDrawer = ({
  open = true,
  onClose,
  initialTab = "offers",
  variant = "drawer",
}) => {
  const isPage = variant === "page";
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const [tab, setTab] = useState(initialTab);
  const [offerForm, setOfferForm] = useState(OFFER_INITIAL);
  const [termsForm, setTermsForm] = useState(termsInitial());
  const [faqForm, setFaqForm] = useState(FAQ_INITIAL);
  const [editingOfferId, setEditingOfferId] = useState(null);
  const [editingTermsId, setEditingTermsId] = useState(null);
  const [editingFaqId, setEditingFaqId] = useState(null);
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [previewOffer, setPreviewOffer] = useState(null);

  useEffect(() => {
    if (!open && !isPage) return;
    setTab(initialTab);
    setOfferForm(OFFER_INITIAL);
    setTermsForm(termsInitial());
    setFaqForm(FAQ_INITIAL);
    setEditingOfferId(null);
    setEditingTermsId(null);
    setEditingFaqId(null);
    setDeleteTarget(null);
  }, [open, initialTab, isPage]);

  const offersQuery = useQuery({
    queryKey: ["referral-program-offers"],
    queryFn: fetchProgramOffers,
    enabled: (open || isPage) && tab === "offers",
    refetchOnWindowFocus: false,
  });
  const termsQuery = useQuery({
    queryKey: ["referral-program-terms"],
    queryFn: fetchProgramTerms,
    enabled: (open || isPage) && tab === "terms",
    refetchOnWindowFocus: false,
  });
  const faqsQuery = useQuery({
    queryKey: ["referral-program-faqs"],
    queryFn: fetchProgramFaqs,
    enabled: (open || isPage) && tab === "faq",
    refetchOnWindowFocus: false,
  });

  const invalidate = (key) => queryClient.invalidateQueries({ queryKey: [key] });

  const offerMutation = useMutation({
    mutationFn: (payload) =>
      editingOfferId ? updateProgramOffer(editingOfferId, payload) : createProgramOffer(payload),
    onSuccess: (_data, variables) => {
      toast.success(editingOfferId ? "Offer updated." : "Offer created.");
      if (variables?.sendViaEmail) {
        toast.info("Email delivery was requested for active partners.");
      }
      setOfferForm(OFFER_INITIAL);
      setEditingOfferId(null);
      invalidate("referral-program-offers");
    },
    onError: (err) => toast.error(errMsg(err, "Could not save offer.")),
  });

  const termsMutation = useMutation({
    mutationFn: (payload) =>
      editingTermsId ? updateProgramTerms(editingTermsId, payload) : createProgramTerms(payload),
    onSuccess: () => {
      toast.success(editingTermsId ? "Terms updated." : "Terms published.");
      setTermsForm(termsInitial());
      setEditingTermsId(null);
      invalidate("referral-program-terms");
    },
    onError: (err) => toast.error(errMsg(err, "Could not save terms.")),
  });

  const faqMutation = useMutation({
    mutationFn: (payload) =>
      editingFaqId ? updateProgramFaq(editingFaqId, payload) : createProgramFaq(payload),
    onSuccess: () => {
      toast.success(editingFaqId ? "FAQ updated." : "FAQ added.");
      setFaqForm(FAQ_INITIAL);
      setEditingFaqId(null);
      invalidate("referral-program-faqs");
    },
    onError: (err) => toast.error(errMsg(err, "Could not save FAQ.")),
  });

  const deleteMutation = useMutation({
    mutationFn: async () => {
      if (!deleteTarget) return;
      if (deleteTarget.kind === "offer") return deleteProgramOffer(deleteTarget.id);
      if (deleteTarget.kind === "terms") return deleteProgramTerms(deleteTarget.id);
      return deleteProgramFaq(deleteTarget.id);
    },
    onSuccess: () => {
      toast.success("Deleted.");
      const key =
        deleteTarget?.kind === "offer"
          ? "referral-program-offers"
          : deleteTarget?.kind === "terms"
          ? "referral-program-terms"
          : "referral-program-faqs";
      invalidate(key);
      setDeleteTarget(null);
    },
    onError: (err) => toast.error(errMsg(err, "Could not delete.")),
  });

  const patchOffer = (e) => {
    const { name, value } = e.target;
    setOfferForm((p) => ({ ...p, [name]: value }));
  };

  const submitOffer = (e) => {
    e.preventDefault();
    if (!offerForm.title.trim()) {
      toast.error("Offer title is required.");
      return;
    }
    if (!offerForm.deadline) {
      toast.error("Deadline is required.");
      return;
    }
    const deadlineProblem = pastDateProblem(
      offerForm.deadline,
      offerForm.originalDeadline,
      "Deadline"
    );
    if (deadlineProblem) {
      toast.error(deadlineProblem);
      return;
    }
    const reward = Number(offerForm.rewardAmount);
    if (!Number.isFinite(reward) || reward <= 0) {
      toast.error("Enter a valid reward amount.");
      return;
    }
    const count = Number(offerForm.targetCount);
    if (!Number.isFinite(count) || count <= 0) {
      toast.error("Enter a valid target count.");
      return;
    }
    offerMutation.mutate({
      title: offerForm.title.trim(),
      description:
        offerForm.description.trim() ||
        `Complete ${count} ${offerForm.targetLabel || "admissions"} before ${
          offerForm.deadline
        } and receive ${offerForm.rewardCurrency} ${reward}.`,
      targetCount: count,
      targetLabel: offerForm.targetLabel.trim() || "admissions",
      deadline: offerForm.deadline,
      rewardAmount: reward,
      rewardCurrency: offerForm.rewardCurrency || "AUD",
      status: offerForm.status,
      ctaLabel: offerForm.ctaLabel.trim() || null,
      imageUrls: Array.isArray(offerForm.imageUrls)
        ? offerForm.imageUrls.filter(Boolean)
        : offerForm.imageUrls
        ? [offerForm.imageUrls]
        : [],
      showOnLoginPopup: Boolean(offerForm.showOnLoginPopup),
      sendViaEmail: Boolean(offerForm.sendViaEmail),
    });
  };

  const startEditOffer = (row) => {
    const images = row.imageUrls || row.images || (row.imageUrl ? [row.imageUrl] : []);
    setEditingOfferId(row.id);
    const deadline = (row.deadline || row.expires || "").slice(0, 10);
    setOfferForm({
      title: row.title || "",
      description: row.description || row.body || "",
      targetCount: String(row.targetCount ?? "3"),
      targetLabel: row.targetLabel || "admissions",
      deadline,
      originalDeadline: deadline,
      rewardAmount: String(row.rewardAmount ?? ""),
      rewardCurrency: row.rewardCurrency || "AUD",
      status: row.status || "ACTIVE",
      ctaLabel: row.ctaLabel || row.cta || "",
      imageUrls: Array.isArray(images) ? images : [],
      showOnLoginPopup: Boolean(row.showOnLoginPopup ?? row.showPopup),
      sendViaEmail: Boolean(row.sendViaEmail ?? row.emailPartners),
    });
  };

  const submitTerms = (e) => {
    e.preventDefault();
    if (!termsForm.version.trim()) {
      toast.error("Version is required (e.g. 2026.2).");
      return;
    }
    if (!termsForm.content.trim()) {
      toast.error("Terms content is required.");
      return;
    }
    const effectiveProblem = pastDateProblem(
      termsForm.effectiveFrom,
      termsForm.originalEffectiveFrom,
      "Effective from"
    );
    if (effectiveProblem) {
      toast.error(effectiveProblem);
      return;
    }
    termsMutation.mutate({
      version: termsForm.version.trim(),
      title: termsForm.title.trim() || "Referral partner terms",
      content: termsForm.content.trim(),
      effectiveFrom: termsForm.effectiveFrom || null,
      isCurrent: Boolean(termsForm.isCurrent),
    });
  };

  const startEditTerms = (row) => {
    const effectiveFrom = (row.effectiveFrom || "").slice(0, 10);
    setEditingTermsId(row.id);
    setTermsForm({
      version: row.version || "",
      title: row.title || "Referral partner terms",
      content: row.content || row.termsHtml || row.body || "",
      effectiveFrom,
      originalEffectiveFrom: effectiveFrom,
      isCurrent: Boolean(row.isCurrent ?? row.current),
      locked: Boolean(row.locked),
      acceptedBy: row.acceptedBy || 0,
    });
  };

  /** Accepted wording is fixed, so changes start a new version that copies it. */
  const startNewVersionFrom = (form) => {
    setEditingTermsId(null);
    setTermsForm({
      ...termsInitial(),
      title: form.title,
      content: form.content,
      isCurrent: true,
    });
  };

  const submitFaq = (e) => {
    e.preventDefault();
    if (!faqForm.question.trim() || !faqForm.answer.trim()) {
      toast.error("Question and answer are required.");
      return;
    }
    faqMutation.mutate({
      question: faqForm.question.trim(),
      answer: faqForm.answer.trim(),
      sortOrder: Number(faqForm.sortOrder) || 0,
      published: Boolean(faqForm.published),
    });
  };

  const startEditFaq = (row) => {
    setEditingFaqId(row.id);
    setFaqForm({
      question: row.question || row.q || "",
      answer: row.answer || row.a || "",
      sortOrder: String(row.sortOrder ?? 0),
      published: row.published !== false,
    });
  };

  const busy =
    offerMutation.isLoading ||
    termsMutation.isLoading ||
    faqMutation.isLoading ||
    deleteMutation.isLoading;

  const tabPanelClass = isPage ? "grid grid-cols-1 xl:grid-cols-2 gap-6 items-start" : "space-y-5";
  const formCardClass = isPage
    ? "space-y-3 rounded-xl border border-app-border bg-app-surface p-5 shadow-sm"
    : "space-y-3 rounded-xl border border-app-border p-4";
  const listCardClass = isPage
    ? "rounded-xl border border-app-border bg-app-surface p-5 shadow-sm space-y-3"
    : "space-y-2";

  const openFullPage = () => {
    onClose?.();
    navigate(`/admin/admin-referral/program${tab ? `?tab=${tab}` : ""}`);
  };

  const headerActions = (
    <div className="flex items-center gap-1 shrink-0">
      {!isPage && (
        <Tooltip title="Open full page">
          <IconButton onClick={openFullPage} disabled={busy} aria-label="Open full page">
            <OpenInFull />
          </IconButton>
        </Tooltip>
      )}
      {!isPage && (
        <IconButton onClick={onClose} disabled={busy} aria-label="Close">
          <Close />
        </IconButton>
      )}
    </div>
  );

  const tabsBar = (
    <Tabs
      value={tab}
      onChange={(_, v) => setTab(v)}
      variant={isPage ? "standard" : "fullWidth"}
      sx={{
        borderBottom: 1,
        borderColor: "divider",
        minHeight: 44,
        "& .MuiTab-root": { minHeight: 44, textTransform: "none", fontWeight: 600 },
      }}
    >
      {TABS.map((t) => (
        <Tab key={t.id} value={t.id} label={t.label} />
      ))}
    </Tabs>
  );

  const contentBody = (
    <div className={isPage ? "px-0 py-0" : "flex-1 overflow-y-auto px-4 py-4"}>
      {tab === "offers" && (
        <div className={tabPanelClass}>
          <div>
            <SectionHeader
              title={editingOfferId ? "Edit offer" : "Create offer"}
              description="Example: complete 3 admissions before 3 July and receive a $500 bonus."
            />
            <form onSubmit={submitOffer} className={formCardClass}>
              <InputField
                label="Title"
                name="title"
                value={offerForm.title}
                onChange={patchOffer}
                required
                placeholder="Winter admissions bonus"
              />
              <TextField
                label="Description"
                name="description"
                value={offerForm.description}
                onChange={patchOffer}
                fullWidth
                size="small"
                multiline
                minRows={2}
                helperText="Leave blank to auto-generate from the fields below."
              />
              <div className="grid grid-cols-2 gap-3">
                <InputField
                  label="Target count"
                  name="targetCount"
                  type="number"
                  value={offerForm.targetCount}
                  onChange={patchOffer}
                  required
                />
                <InputField
                  label="Target unit"
                  name="targetLabel"
                  value={offerForm.targetLabel}
                  onChange={patchOffer}
                  placeholder="admissions"
                />
              </div>
              <div className="grid grid-cols-2 gap-3">
                <TextField
                  label="Deadline"
                  name="deadline"
                  type="date"
                  value={offerForm.deadline}
                  onChange={patchOffer}
                  fullWidth
                  size="small"
                  required
                  InputLabelProps={{ shrink: true }}
                  inputProps={{ min: minDateFor(offerForm.deadline, offerForm.originalDeadline) }}
                />
                <TextField
                  select
                  label="Status"
                  name="status"
                  value={offerForm.status}
                  onChange={patchOffer}
                  fullWidth
                  size="small"
                >
                  <MenuItem value="DRAFT">Draft</MenuItem>
                  <MenuItem value="ACTIVE">Active</MenuItem>
                  <MenuItem value="ENDED">Ended</MenuItem>
                </TextField>
              </div>
              <div className="grid grid-cols-2 gap-3">
                <InputField
                  label="Bonus amount"
                  name="rewardAmount"
                  type="number"
                  value={offerForm.rewardAmount}
                  onChange={patchOffer}
                  required
                  placeholder="500"
                />
                <div className="w-full">
                  <labe className="block uppercase text-app-text-secondary text-xs font-semibold mb-2">
                    Currency
                  </labe>
                  <TextField
                    select
                    label="Currency"
                    name="rewardCurrency"
                    value={offerForm.rewardCurrency}
                    onChange={patchOffer}
                    fullWidth
                    size="small"
                  >
                    <MenuItem value="AUD">AUD</MenuItem>
                    <MenuItem value="NPR">NPR</MenuItem>
                    <MenuItem value="USD">USD</MenuItem>
                  </TextField>
                </div>
              </div>
              <InputField
                label="Popup button text (optional)"
                name="ctaLabel"
                value={offerForm.ctaLabel}
                onChange={patchOffer}
                placeholder="Got it"
              />
              <p className="-mt-2 mb-0 text-xs text-app-text-muted">
                The button that closes this offer&apos;s sign-in popup. Leave blank for &quot;Got
                it&quot;.
              </p>

              <div className="rounded-lg border border-app-border bg-app-surface-muted/40 p-3 space-y-1">
                <Typography variant="subtitle2" className="font-semibold text-app-text mb-1">
                  Offer images
                </Typography>
                <Typography
                  variant="caption"
                  color="text.secondary"
                  display="block"
                  className="mb-2"
                >
                  Banner or promo images partners see on the popup / email.
                </Typography>
                <UploadFile
                  data={offerForm}
                  setData={setOfferForm}
                  imageKey="imageUrls"
                  type="multiple"
                  label="Offer images"
                  newDesign
                />
              </div>

              <div className="rounded-lg border border-app-border p-3 space-y-1">
                <Typography variant="subtitle2" className="font-semibold text-app-text">
                  Delivery
                </Typography>
                <FormControlLabel
                  control={
                    <Switch
                      checked={Boolean(offerForm.showOnLoginPopup)}
                      onChange={(e) =>
                        setOfferForm((p) => ({ ...p, showOnLoginPopup: e.target.checked }))
                      }
                      color="primary"
                    />
                  }
                  label={
                    <span>
                      <span className="text-sm font-medium">Show popup on partner login</span>
                      <span className="block text-xs text-app-text-muted">
                        Opens a modal when they enter the partner dashboard
                      </span>
                    </span>
                  }
                />
                <FormControlLabel
                  control={
                    <Switch
                      checked={Boolean(offerForm.sendViaEmail)}
                      onChange={(e) =>
                        setOfferForm((p) => ({ ...p, sendViaEmail: e.target.checked }))
                      }
                      color="primary"
                    />
                  }
                  label={
                    <span>
                      <span className="text-sm font-medium">Send via email</span>
                      <span className="block text-xs text-app-text-muted">
                        Email all active referral partners when this offer is posted/updated
                      </span>
                    </span>
                  }
                />
              </div>

              <div className="flex gap-2 justify-end pt-1">
                {editingOfferId ? (
                  <Button
                    type="button"
                    onClick={() => {
                      setEditingOfferId(null);
                      setOfferForm(OFFER_INITIAL);
                    }}
                  >
                    Cancel edit
                  </Button>
                ) : null}
                <Button
                  type="submit"
                  variant="contained"
                  disabled={offerMutation.isLoading}
                  startIcon={
                    offerMutation.isLoading ? <CircularProgress size={14} color="inherit" /> : null
                  }
                >
                  {editingOfferId ? "Update offer" : "Post offer"}
                </Button>
              </div>
            </form>
          </div>

          <div className={listCardClass}>
            <SectionHeader title="Published offers" />
            {offersQuery.isLoading ? (
              <ListSkeleton />
            ) : offersQuery.isError ? (
              <EmptyHint>Could not load offers. Check the API is available.</EmptyHint>
            ) : (offersQuery.data || []).length === 0 ? (
              <EmptyHint>No offers yet — create one above.</EmptyHint>
            ) : (
              <ul className="m-0 p-0 list-none space-y-2 max-h-[36rem] overflow-y-auto pr-1">
                {(offersQuery.data || []).map((row) => {
                  const thumbs =
                    row.imageUrls || row.images || (row.imageUrl ? [row.imageUrl] : []);
                  return (
                    <li
                      key={row.id}
                      className="rounded-xl border border-app-border px-3 py-3 flex gap-2 justify-between"
                    >
                      <div className="min-w-0 flex gap-3">
                        {thumbs?.[0] ? (
                          <img
                            src={thumbs[0]}
                            alt=""
                            className="h-14 w-14 rounded-lg object-cover border border-app-border shrink-0"
                          />
                        ) : null}
                        <div className="min-w-0">
                          <div className="flex items-center gap-2 flex-wrap">
                            <p className="text-sm font-semibold m-0 break-words line-clamp-1">
                              {row.title}
                            </p>
                            <Chip size="small" label={row.status || "ACTIVE"} />
                            {(row.showOnLoginPopup ?? row.showPopup) && (
                              <Chip
                                size="small"
                                icon={<CampaignOutlined sx={{ fontSize: "14px !important" }} />}
                                label="Login popup"
                                variant="outlined"
                                color="primary"
                              />
                            )}
                            {(row.sendViaEmail ?? row.emailPartners) && (
                              <Chip
                                size="small"
                                icon={<EmailOutlined sx={{ fontSize: "14px !important" }} />}
                                label="Email"
                                variant="outlined"
                              />
                            )}
                          </div>
                          <p className="text-xs text-app-text-muted m-0 mt-1 break-words line-clamp-2">
                            {row.description ||
                              row.body ||
                              `${row.targetCount || "—"} ${row.targetLabel || "admissions"} by ${
                                row.deadline || row.expires || "—"
                              } · ${row.rewardCurrency || "AUD"} ${row.rewardAmount ?? "—"}`}
                          </p>
                        </div>
                      </div>
                      <div className="flex shrink-0 items-start">
                        <Tooltip title="View details">
                          <IconButton
                            size="small"
                            onClick={() => setPreviewOffer(row)}
                            aria-label="View details"
                          >
                            <VisibilityOutlined fontSize="small" />
                          </IconButton>
                        </Tooltip>
                        <IconButton
                          size="small"
                          onClick={() => startEditOffer(row)}
                          aria-label="Edit"
                        >
                          <EditOutlined fontSize="small" />
                        </IconButton>
                        <IconButton
                          size="small"
                          color="error"
                          onClick={() =>
                            setDeleteTarget({ kind: "offer", id: row.id, label: row.title })
                          }
                          aria-label="Delete"
                        >
                          <DeleteOutline fontSize="small" />
                        </IconButton>
                      </div>
                    </li>
                  );
                })}
              </ul>
            )}
          </div>
        </div>
      )}

      {tab === "terms" && (
        <div className={tabPanelClass}>
          <div>
            <SectionHeader
              title={editingTermsId ? "Edit terms version" : "Publish terms & conditions"}
              description="Partners accept the current version during onboarding and can review it in Settings."
            />
            <form onSubmit={submitTerms} className={formCardClass}>
              {termsForm.locked ? (
                <Alert
                  severity="info"
                  action={
                    <Button
                      color="inherit"
                      size="small"
                      onClick={() => startNewVersionFrom(termsForm)}
                    >
                      Create new version
                    </Button>
                  }
                >
                  Accepted by {termsForm.acceptedBy}{" "}
                  {termsForm.acceptedBy === 1 ? "partner" : "partners"}, so this wording is fixed.
                  You can still change whether it is the current version. To change the wording,
                  create a new version from it.
                </Alert>
              ) : null}
              <div className="grid grid-cols-2 gap-3">
                <InputField
                  label="Version"
                  name="version"
                  disabled={termsForm.locked}
                  value={termsForm.version}
                  onChange={(e) => setTermsForm((p) => ({ ...p, version: e.target.value }))}
                  required
                  placeholder="2026.2"
                />
                <div className="w-full">
                  <label className="block uppercase text-app-text-secondary text-xs font-semibold mb-2">
                    Effective from
                  </label>
                  <TextField
                    label="Effective from"
                    type="date"
                    value={termsForm.effectiveFrom}
                    onChange={(e) => setTermsForm((p) => ({ ...p, effectiveFrom: e.target.value }))}
                    fullWidth
                    size="small"
                    disabled={termsForm.locked}
                    InputLabelProps={{ shrink: true }}
                    inputProps={{
                      min: minDateFor(termsForm.effectiveFrom, termsForm.originalEffectiveFrom),
                    }}
                  />
                </div>
              </div>
              <InputField
                label="Title"
                name="title"
                disabled={termsForm.locked}
                value={termsForm.title}
                onChange={(e) => setTermsForm((p) => ({ ...p, title: e.target.value }))}
              />
              <TextField
                label="Terms content"
                disabled={termsForm.locked}
                value={termsForm.content}
                onChange={(e) => setTermsForm((p) => ({ ...p, content: e.target.value }))}
                fullWidth
                size="small"
                multiline
                minRows={8}
                required
                placeholder="Paste or write the full partner terms…"
              />
              <FormControlLabel
                control={
                  <Switch
                    checked={Boolean(termsForm.isCurrent)}
                    onChange={(e) => setTermsForm((p) => ({ ...p, isCurrent: e.target.checked }))}
                  />
                }
                label="Mark as current version"
              />
              <div className="flex gap-2 justify-end">
                {editingTermsId ? (
                  <Button
                    type="button"
                    onClick={() => {
                      setEditingTermsId(null);
                      setTermsForm(termsInitial());
                    }}
                  >
                    Cancel edit
                  </Button>
                ) : null}
                <Button
                  type="submit"
                  variant="contained"
                  disabled={termsMutation.isLoading}
                  startIcon={
                    termsMutation.isLoading ? <CircularProgress size={14} color="inherit" /> : null
                  }
                >
                  {editingTermsId ? "Update terms" : "Post terms"}
                </Button>
              </div>
            </form>
          </div>

          <div className={listCardClass}>
            <SectionHeader title="Terms history" />
            {termsQuery.isLoading ? (
              <ListSkeleton />
            ) : termsQuery.isError ? (
              <EmptyHint>Could not load terms. Check the API is available.</EmptyHint>
            ) : (termsQuery.data || []).length === 0 ? (
              <EmptyHint>No terms versions yet.</EmptyHint>
            ) : (
              <ul className="m-0 p-0 list-none space-y-2 max-h-[36rem] overflow-y-auto pr-1">
                {(termsQuery.data || []).map((row) => (
                  <li
                    key={row.id}
                    className="rounded-xl border border-app-border px-3 py-3 flex gap-2 justify-between"
                  >
                    <div className="min-w-0">
                      <div className="flex items-center gap-2 flex-wrap">
                        <p className="text-sm font-semibold m-0">
                          {row.title || "Terms"} · v{row.version}
                        </p>
                        {(row.isCurrent || row.current) && (
                          <Chip size="small" color="success" label="Current" />
                        )}
                        {row.acceptedBy ? (
                          <Chip
                            size="small"
                            variant="outlined"
                            label={`Accepted by ${row.acceptedBy} · locked`}
                          />
                        ) : null}
                      </div>
                      <p className="text-xs text-app-text-muted m-0 mt-1 line-clamp-2">
                        {row.content || row.termsHtml || row.body || "—"}
                      </p>
                    </div>
                    <div className="flex shrink-0">
                      <IconButton
                        size="small"
                        aria-label={row.locked ? "Open locked version" : "Edit"}
                        onClick={() => startEditTerms(row)}
                      >
                        <EditOutlined fontSize="small" />
                      </IconButton>
                      {row.locked ? null : (
                        <IconButton
                          size="small"
                          color="error"
                          aria-label="Delete"
                          onClick={() =>
                            setDeleteTarget({
                              kind: "terms",
                              id: row.id,
                              label: `v${row.version}`,
                            })
                          }
                        >
                          <DeleteOutline fontSize="small" />
                        </IconButton>
                      )}
                    </div>
                  </li>
                ))}
              </ul>
            )}
          </div>
        </div>
      )}

      {tab === "faq" && (
        <div className={tabPanelClass}>
          <div>
            <SectionHeader
              title={editingFaqId ? "Edit FAQ" : "Add FAQ"}
              description="Shown in the partner Help / FAQ area."
            />
            <form onSubmit={submitFaq} className={formCardClass}>
              <InputField
                label="Question"
                value={faqForm.question}
                onChange={(e) => setFaqForm((p) => ({ ...p, question: e.target.value }))}
                required
                placeholder="When do I get paid?"
              />
              <TextField
                label="Answer"
                value={faqForm.answer}
                onChange={(e) => setFaqForm((p) => ({ ...p, answer: e.target.value }))}
                fullWidth
                size="small"
                multiline
                minRows={3}
                required
              />
              <div className="flex flex-wrap items-center gap-3 justify-between">
                <InputField
                  label="Sort order"
                  type="number"
                  value={faqForm.sortOrder}
                  onChange={(e) => setFaqForm((p) => ({ ...p, sortOrder: e.target.value }))}
                  outerClassName="w-32"
                />
                <FormControlLabel
                  control={
                    <Switch
                      checked={Boolean(faqForm.published)}
                      onChange={(e) => setFaqForm((p) => ({ ...p, published: e.target.checked }))}
                    />
                  }
                  label="Published"
                />
              </div>
              <div className="flex gap-2 justify-end">
                {editingFaqId ? (
                  <Button
                    type="button"
                    onClick={() => {
                      setEditingFaqId(null);
                      setFaqForm(FAQ_INITIAL);
                    }}
                  >
                    Cancel edit
                  </Button>
                ) : null}
                <Button
                  type="submit"
                  variant="contained"
                  disabled={faqMutation.isLoading}
                  startIcon={
                    faqMutation.isLoading ? <CircularProgress size={14} color="inherit" /> : null
                  }
                >
                  {editingFaqId ? "Update FAQ" : "Post FAQ"}
                </Button>
              </div>
            </form>
          </div>

          <div className={listCardClass}>
            <SectionHeader title="FAQ list" />
            {faqsQuery.isLoading ? (
              <ListSkeleton />
            ) : faqsQuery.isError ? (
              <EmptyHint>Could not load FAQs. Check the API is available.</EmptyHint>
            ) : (faqsQuery.data || []).length === 0 ? (
              <EmptyHint>No FAQs yet.</EmptyHint>
            ) : (
              <ul className="m-0 p-0 list-none space-y-2">
                {(faqsQuery.data || []).map((row) => (
                  <li
                    key={row.id}
                    className="rounded-xl border border-app-border px-3 py-3 flex gap-2 justify-between"
                  >
                    <div className="min-w-0">
                      <div className="flex items-center gap-2">
                        <p className="text-sm font-semibold m-0">{row.question || row.q}</p>
                        {row.published === false && (
                          <Chip size="small" label="Hidden" variant="outlined" />
                        )}
                      </div>
                      <p className="text-xs text-app-text-muted m-0 mt-1 line-clamp-2">
                        {row.answer || row.a}
                      </p>
                    </div>
                    <div className="flex shrink-0">
                      <IconButton size="small" onClick={() => startEditFaq(row)}>
                        <EditOutlined fontSize="small" />
                      </IconButton>
                      <IconButton
                        size="small"
                        color="error"
                        onClick={() =>
                          setDeleteTarget({
                            kind: "faq",
                            id: row.id,
                            label: row.question || row.q,
                          })
                        }
                      >
                        <DeleteOutline fontSize="small" />
                      </IconButton>
                    </div>
                  </li>
                ))}
              </ul>
            )}
          </div>
        </div>
      )}
    </div>
  );

  const shell = isPage ? (
    <div className="flex flex-wrap mt-4 dashBody">
      <div className="w-full mb-12 px-4">
        <div className="relative flex flex-col min-w-0 break-words w-full mb-6 shadow-lg rounded bg-app-surface text-app-text overflow-hidden">
          <div className="px-4 md:px-6 py-4 border-b border-app-border bg-gradient-to-r from-app-surface-alt to-app-surface-muted">
            <div className="flex flex-wrap items-start justify-between gap-3">
              <div className="flex items-start gap-3 min-w-0">
                <IconButton
                  onClick={() => navigate("/admin/admin-referral")}
                  aria-label="Back to referrals"
                  size="small"
                >
                  <Close />
                </IconButton>
                <div>
                  <Typography variant="h5" className="font-semibold text-app-text">
                    Partner program content
                  </Typography>
                  <Typography variant="body2" color="text.secondary" className="mt-0.5">
                    Create offers, terms and FAQs — form on the left, published list on the right.
                  </Typography>
                </div>
              </div>
              <Button
                variant="outlined"
                size="small"
                onClick={() => navigate("/admin/admin-referral")}
              >
                Back to referrals
              </Button>
            </div>
            <div className="mt-3">{tabsBar}</div>
          </div>
          <div className="px-4 md:px-6 py-5 bg-app-surface-muted/40 min-h-[70vh]">
            {contentBody}
          </div>
        </div>
      </div>
    </div>
  ) : (
    <Drawer anchor="right" open={open} onClose={busy ? undefined : onClose}>
      <div className="w-full sm:w-[560px] max-w-[100vw] h-full flex flex-col bg-app-surface text-app-text">
        <div className="flex items-start justify-between gap-3 px-4 py-3 border-b border-app-border">
          <div>
            <Typography variant="h6" className="font-semibold">
              Partner program content
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
              Publish offers, terms and FAQs for referral partners.
            </Typography>
          </div>
          {headerActions}
        </div>
        {tabsBar}
        {contentBody}
        <Box className="px-4 py-3 border-t border-app-border">
          <Button fullWidth variant="outlined" onClick={onClose} disabled={busy}>
            Close
          </Button>
        </Box>
      </div>
    </Drawer>
  );

  return (
    <>
      {shell}
      <ProgramOfferPreviewDialog
        offer={previewOffer}
        onClose={() => setPreviewOffer(null)}
        onEdit={(row) => {
          setPreviewOffer(null);
          startEditOffer(row);
        }}
      />
      <Dialog open={Boolean(deleteTarget)} onClose={() => setDeleteTarget(null)}>
        <DialogTitle>Delete {deleteTarget?.kind}?</DialogTitle>
        <DialogContent>
          <Typography variant="body2">
            Remove <strong>{deleteTarget?.label}</strong>? Partners will no longer see it.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteTarget(null)}>Cancel</Button>
          <Button
            color="error"
            variant="contained"
            disabled={deleteMutation.isLoading}
            onClick={() => deleteMutation.mutate()}
          >
            Delete
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
};

export default ReferralProgramContentDrawer;
